import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Alert, Box, Breadcrumbs, Button, CircularProgress, IconButton, InputAdornment, Link, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography } from "@mui/material";
import { Folder, FolderPlus, Upload, FileText, Image as ImageIcon, Trash2, Search, Download } from "lucide-react";
import { EmptyState, GlassCard, T, TYPE, formatRelative, labelSx, primaryButtonSx, ghostButtonSx } from "../../user/complaints/shared";
import { useSocketEvent } from "../../helpdesk/socket";
import { fileSize } from "../../helpdesk/ui";
import { pm, errorMessage, openFile, fileViewUrl } from "../api";
import { usePm } from "../PmContext";
import { has } from "../ui";
import { fieldSx } from "../inputs";

const Files = ({ project }) => {
  const ctx = usePm();
  const [folder, setFolder] = useState("");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [extraFolders, setExtraFolders] = useState([]);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);
  const canUpload = has(project, "issue.attach") && !project.archivedAt;

  const load = useCallback(async () => {
    try {
      setData(await pm.files(project._id, { folder, q: q || undefined }));
      setError("");
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [project._id, folder, q]);
  useEffect(() => {
    const t = setTimeout(load, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, q]);
  useSocketEvent("pm.files.changed", (p) => String(p?.projectId) === String(project._id) && load());

  const upload = async (files) => {
    if (!files.length) return;
    setBusy(true);
    try {
      await pm.uploadFiles(project._id, files, folder);
      ctx.notify(`${files.length} file(s) uploaded`);
      load();
    } catch (err) {
      ctx.fail(err, "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const allFolders = [...new Set([...(data?.folders || []), ...extraFolders])];
  const subfolders = [...new Set(allFolders.filter((f) => (folder ? f.startsWith(`${folder}/`) : true)).map((f) => (folder ? f.slice(folder.length + 1) : f).split("/")[0]).filter(Boolean))];
  const parts = folder ? folder.split("/") : [];

  return (
    <Box onDragOver={(e) => { if (canUpload) { e.preventDefault(); setDrag(true); } }} onDragLeave={() => setDrag(false)} onDrop={(e) => { e.preventDefault(); setDrag(false); if (canUpload) upload([...e.dataTransfer.files]); }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} alignItems={{ md: "center" }} sx={{ mb: 2 }}>
        <Breadcrumbs sx={{ flexGrow: 1 }}>
          <Link component="button" underline="hover" onClick={() => setFolder("")} sx={{ fontSize: TYPE.small, fontWeight: 600 }}>All files</Link>
          {parts.map((p, i) => <Link key={i} component="button" underline="hover" onClick={() => setFolder(parts.slice(0, i + 1).join("/"))} sx={{ fontSize: TYPE.small }}>{p}</Link>)}
        </Breadcrumbs>
        <TextField size="small" placeholder="Search files" value={q} onChange={(e) => setQ(e.target.value)} sx={{ ...fieldSx, width: 200 }} InputProps={{ startAdornment: <InputAdornment position="start"><Search size={14} /></InputAdornment> }} />
        {canUpload && <Button size="small" variant="outlined" sx={ghostButtonSx} startIcon={<FolderPlus size={14} />} onClick={() => { const n = window.prompt("Folder name"); if (n?.trim()) { const f = folder ? `${folder}/${n.trim()}` : n.trim(); setExtraFolders((x) => [...x, f]); setFolder(f); } }}>New folder</Button>}
        {canUpload && <Button size="small" variant="contained" sx={primaryButtonSx} startIcon={busy ? <CircularProgress size={12} color="inherit" /> : <Upload size={14} />} disabled={busy} onClick={() => inputRef.current?.click()}>Upload</Button>}
        <input ref={inputRef} type="file" hidden multiple onChange={(e) => { upload([...e.target.files]); e.target.value = ""; }} />
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <GlassCard sx={{ outline: drag ? `2px dashed ${T.brand}` : "none", overflowX: "auto" }}>
        {!data ? <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={22} /></Box> : !data.files.length && !subfolders.length ? (
          <EmptyState icon={Folder} title={q ? "No files match" : "No files here"} description={canUpload ? "Drop files anywhere on this page or use Upload. Attachments on issues also appear in the Issues folder." : "Nothing has been shared yet."} />
        ) : (
          <Table size="small">
            <TableHead><TableRow>{["Name", "Size", "Issue", "Uploaded by", "When", ""].map((h) => <TableCell key={h} sx={labelSx}>{h}</TableCell>)}</TableRow></TableHead>
            <TableBody>
              {!q && subfolders.map((f) => (
                <TableRow key={f} hover sx={{ cursor: "pointer" }} onClick={() => setFolder(folder ? `${folder}/${f}` : f)}>
                  <TableCell colSpan={6}><Stack direction="row" spacing={1} alignItems="center"><Folder size={16} color="#F79009" /><Typography sx={{ fontSize: TYPE.small, fontWeight: 600 }}>{f}</Typography></Stack></TableCell>
                </TableRow>
              ))}
              {data.files.map((f) => (
                <TableRow key={f._id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ cursor: "pointer" }} onClick={() => openFile(f)}>
                      {f.viewToken && f.mimeType.startsWith("image/") ? <Box component="img" src={fileViewUrl(f.viewToken)} alt="" sx={{ width: 32, height: 32, objectFit: "cover", borderRadius: "4px" }} /> : f.mimeType.startsWith("image/") ? <ImageIcon size={16} /> : <FileText size={16} color={T.inkSecondary} />}
                      <Typography sx={{ fontSize: TYPE.small, fontWeight: 500 }}>{f.fileName}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ fontSize: TYPE.micro }}>{fileSize(f.size)}</TableCell>
                  <TableCell sx={{ fontSize: TYPE.micro }}>{f.issueId ? <IssueLink project={project} issueId={f.issueId} /> : "—"}</TableCell>
                  <TableCell sx={{ fontSize: TYPE.micro }}>{f.uploadedBy?.name}</TableCell>
                  <TableCell sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{formatRelative(f.createdAt)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <Tooltip title="Download"><IconButton size="small" onClick={() => openFile(f)}><Download size={14} /></IconButton></Tooltip>
                    {!project.archivedAt && (String(f.uploadedBy?.id) === String(ctx.me.id) || has(project, "project.manage_settings")) && (
                      <Tooltip title="Delete"><IconButton size="small" onClick={async () => { if (window.confirm(`Delete ${f.fileName}?`)) { try { await pm.deleteFile(f._id); load(); } catch (err) { ctx.fail(err); } } }}><Trash2 size={14} /></IconButton></Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </GlassCard>
    </Box>
  );
};

const keyCache = new Map();
const IssueLink = ({ project, issueId }) => {
  const [key, setKey] = useState(keyCache.get(String(issueId)));
  useEffect(() => {
    if (key) return;
    pm.issue(issueId).then((i) => { keyCache.set(String(issueId), i.key); setKey(i.key); }).catch(() => setKey("—"));
  }, [issueId, key]);
  return key && key !== "—" ? <Typography component={RouterLink} to={`/projects/${project.key}/issues/${key}`} sx={{ fontSize: TYPE.micro, color: T.brand }}>{key}</Typography> : <span>{key || "…"}</span>;
};

export default Files;
